import { ActionIcon, AppShell, Group, NavLink, Text, UnstyledButton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowLeft, IconChecklist, IconHome, IconPackages, IconSettings, type Icon } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { NavLink as RouterNavLink, Outlet, useLocation, useNavigate } from "react-router";
import classes from "./AppLayout.module.css";

type NavItem = { to: string; label: "nav.activity" | "nav.tasks" | "nav.stock" | "nav.settings"; icon: Icon };

// Sekce podle Patrikova návrhu (F-DSH-02); Menu přibude ve fázi 2.
const navItems: NavItem[] = [
  { to: "/", label: "nav.activity", icon: IconHome },
  { to: "/ukoly", label: "nav.tasks", icon: IconChecklist },
  { to: "/sklady", label: "nav.stock", icon: IconPackages },
  { to: "/nastaveni", label: "nav.settings", icon: IconSettings },
];

/** Rozvržení: na mobilu spodní navigace, na desktopu boční panel. */
export function AppLayout() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 48em)", true, { getInitialValueInEffect: false });
  const { pathname, key } = useLocation();
  const isActive = (to: string) => (to === "/" ? pathname === "/" : pathname.startsWith(to));
  const navigate = useNavigate();
  const isTopLevel = navItems.some((item) => item.to === pathname);

  // Zpět v historii; po přímém otevření odkazu (bez historie v aplikaci) o úroveň výš v adrese.
  const goBack = () => {
    if (key !== "default") {
      navigate(-1);
      return;
    }
    const parent = pathname.replace(/\/[^/]+\/?$/, "");
    navigate(parent || "/");
  };

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: "sm", collapsed: { mobile: true } }}
      footer={{ height: 68, collapsed: !isMobile }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" gap="xs" wrap="nowrap">
          {!isTopLevel && (
            <ActionIcon variant="subtle" size="lg" color="gray" onClick={goBack} aria-label={t("nav.back")}>
              <IconArrowLeft size={24} />
            </ActionIcon>
          )}
          <Text fw={700} size="lg">
            {t("app.name")}
          </Text>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            component={RouterNavLink}
            to={item.to}
            label={t(item.label)}
            leftSection={<item.icon size={22} stroke={1.75} />}
            active={isActive(item.to)}
            py="md"
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>

      <AppShell.Footer className={classes.bottomNav}>
        {navItems.map((item) => (
          <UnstyledButton
            key={item.to}
            component={RouterNavLink}
            to={item.to}
            className={classes.bottomNavItem}
            data-active={isActive(item.to) || undefined}
          >
            <item.icon size={26} stroke={1.75} />
            <span>{t(item.label)}</span>
          </UnstyledButton>
        ))}
      </AppShell.Footer>
    </AppShell>
  );
}
